import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { Nav } from "@/components/Nav";
import { MobileTabBar } from "@/components/MobileTabBar";
import { NavSpacer } from "@/components/NavSpacer";
import { Footer } from "@/components/Footer";
import { CookieBar } from "@/components/CookieBar";
import { BackButton } from "@/components/BackButton";
import Home from "@/pages/Home";
import Plans from "@/pages/Plans";
import NationalGame from "@/pages/NationalGame";
import HallOfFame from "@/pages/HallOfFame";
import Leaderboard from "@/pages/Leaderboard";
import PlayerDetail from "@/pages/PlayerDetail";
import NewsIndex from "@/pages/NewsIndex";
import NewsDetail from "@/pages/NewsDetail";
import MilestoneUpdates from "@/pages/MilestoneUpdates";
import Counties from "@/pages/Counties";
import Facilities from "@/pages/Facilities";
import Money from "@/pages/Money";
import Trading from "@/pages/Trading";
import HealthAndFitness from "@/pages/HealthAndFitness";
import CountyDossier from "@/pages/CountyDossier";
import CountyOnePager from "@/pages/CountyOnePager";
import CountyCompareSheet from "@/pages/CountyCompareSheet";
import NationalRollup from "@/pages/NationalRollup";
import EraCohort from "@/pages/EraCohort";
import Shortlist from "@/pages/Shortlist";
import CountyCompareTwo from "@/pages/CountyCompareTwo";
import ValueCase from "@/pages/ValueCase";
import Impact from "@/pages/Impact";
import Firsts from "@/pages/Firsts";
import RouteLanding from "@/pages/RouteLanding";
import FcNairobi from "@/pages/FcNairobi";
import LegalDoc from "@/pages/LegalDoc";
import EcosystemPage from "@/pages/Ecosystem";
import WomensGame from "@/pages/WomensGame";
import WomanDetail from "@/pages/WomanDetail";
import Opportunities from "@/pages/Opportunities";
import WhatIf from "@/pages/WhatIf";
import { Valuations, Observations, Legal } from "@/pages/DocPages";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import StubPage from "@/pages/StubPage";
import Sources from "@/pages/Sources";
import CourtRollout from "@/pages/CourtRollout";
import GovernorsLeague from "@/pages/GovernorsLeague";
import ProfessionalsJoin from "@/pages/ProfessionalsJoin";
import Professionals from "@/pages/Professionals";
import SpecimenTerms from "@/pages/SpecimenTerms";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import LandComparables from "@/pages/LandComparables";
import Licences from "@/pages/Licences";
import SportsLaw from "@/pages/SportsLaw";
import Contribute from "@/pages/Contribute";
import FifaRanking from "@/pages/FifaRanking";
import FkfFeed from "@/pages/FkfFeed";
import RankingAmbition from "@/pages/RankingAmbition";
import RankingAlertStop from "@/pages/RankingAlertStop";
import HarambeeFixtures from "@/pages/HarambeeFixtures";
import FootballEconomy from "@/pages/FootballEconomy";
import { GlobalDragRails } from "@/components/ScrollRail";

import { AuthProvider } from "@/admin/AuthContext";
import AdminLogin from "@/admin/AdminLogin";
import AdminLayout from "@/admin/AdminLayout";
import Dashboard from "@/admin/Dashboard";
import DataHealth from "@/admin/DataHealth";
import ResourceAdmin from "@/admin/ResourceAdmin";
import Submissions from "@/admin/Submissions";
import Intake from "@/admin/Intake";
import Applications from "@/admin/Applications";
import ModularStadiums from "@/pages/ModularStadiums";
import Apply from "@/pages/Apply";
import { ProposalStrip } from "@/components/ProposalStrip";
import PitchSizing from "@/pages/PitchSizing";
import LeaseComparison from "@/pages/LeaseComparison";
import SiteAppraisal from "@/pages/SiteAppraisal";
import LandValue from "@/pages/LandValue";
import IdleAcre from "@/pages/IdleAcre";
import CompareFacilities from "@/pages/CompareFacilities";
import LandAlertStop from "@/pages/LandAlertStop";
import FacilityPlanner from "@/pages/FacilityPlanner";
import LandAudit from "@/pages/LandAudit";
import Discoveries from "@/admin/Discoveries";
import BioReview from "@/admin/BioReview";
import KenyaEntity from "@/admin/KenyaEntity";
import AdminLicences from "@/admin/Licences";
import AdminProjects from "@/admin/Projects";
import ConsentLog from "@/admin/ConsentLog";
import SiteSettings from "@/admin/SiteSettings";
import SearchConsole from "@/admin/SearchConsole";

const STUBS = [
  ["careers", "Careers", "Work with us", "Role profiles for coaches, analysts, physiotherapists and commercial staff are published as guidance on our training and jobs page. There are no live vacancies yet — recruitment will be announced in football development updates."],
  ["refugees-in-football", "Refugees in Football", "Inclusion", "Leagues and coaching inside and around Kakuma and Dadaab, treating football as a route to belonging. Proposed programme: not yet running."],
  ["women-in-football", "Women in Football", "Equal from day one", "Equal facility hours, equal coaching hours and equal camp days for girls and women wherever a county academy or court is built. This is a commitment we publish, not a separate project or an academy: there is nothing open or recruiting."],
  ["grassroots", "Football Projects and Activities", "Coming Soon", "County leagues, futsal courts and coach education, published project by project once the first sites are agreed. Nothing is running yet and we are not taking player registrations."],
  ["disabilities-in-football", "Disabilities in Football", "Inclusion", "Para and inclusive football: coaching, competition and facility access planned alongside every county academy, so disability is never the reason a player stops playing. These are proposed programmes, not services running today."],
];

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) return el.scrollIntoView({ behavior: "smooth" });
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
};


const RenamedShortlist = () => {
  const { token } = useParams();
  return <Navigate to={`/county-scenarios/${token}`} replace />;
};

const PublicShell = ({ children }) => (
  <>
    <Nav />
    <NavSpacer />
    <BackButton />
    <main>{children}</main>
    <ProposalStrip />
    <Footer />
    <CookieBar />
    <div className="h-[88px] md:hidden" aria-hidden="true" />
    <MobileTabBar />
  </>
);

const page = (element) => <PublicShell>{element}</PublicShell>;

function App() {
  return (
    <div className="App dark min-h-screen overflow-x-hidden bg-[#050505] text-white">
      <BrowserRouter>
        <ScrollToTop />
        <GlobalDragRails />
        <AuthProvider>
          <Routes>
            <Route path="/" element={page(<Home />)} />
            <Route path="/plans" element={page(<Plans />)} />
            <Route path="/national-game" element={page(<NationalGame />)} />
            <Route path="/hall-of-fame" element={page(<HallOfFame />)} />
            <Route path="/hall-of-fame/leaderboard" element={page(<Leaderboard />)} />
            <Route path="/leaderboard" element={page(<Leaderboard />)} />
            <Route path="/hall-of-fame/:slug" element={page(<PlayerDetail />)} />
            <Route path="/news" element={page(<NewsIndex />)} />
            <Route path="/news/:slug" element={page(<NewsDetail />)} />
            <Route path="/updates" element={page(<MilestoneUpdates />)} />
            <Route path="/counties" element={page(<Counties />)} />
            <Route path="/facilities" element={page(<Facilities />)} />
            <Route path="/money" element={page(<Money />)} />
            <Route path="/trading" element={page(<Trading />)} />
            <Route path="/phasing" element={<Navigate to="/plans?view=phasing" replace />} />
            <Route path="/health-and-fitness" element={page(<HealthAndFitness />)} />
            <Route path="/counties/rollup" element={page(<NationalRollup />)} />
            <Route path="/eras/:slug" element={page(<EraCohort />)} />
            <Route path="/counties/compare-sheet" element={page(<CountyCompareSheet />)} />
            <Route path="/counties/:slug/one-page" element={page(<CountyOnePager />)} />
            <Route path="/counties/:slug" element={page(<CountyDossier />)} />
            <Route path="/county-scenarios" element={page(<Shortlist />)} />
            <Route path="/county-scenarios/:token" element={page(<Shortlist />)} />
            <Route path="/investor-shortlist" element={<Navigate to="/county-scenarios" replace />} />
            <Route path="/investor-shortlist/:token" element={<RenamedShortlist />} />
            <Route path="/compare" element={page(<CountyCompareTwo />)} />
            <Route path="/value-case" element={page(<ValueCase />)} />
            <Route path="/impact" element={page(<Impact />)} />
            <Route path="/impact/:slug" element={page(<Impact />)} />
            <Route path="/firsts" element={page(<Firsts />)} />
            <Route path="/fc-nairobi" element={page(<FcNairobi />)} />
            <Route path="/privacy" element={page(<LegalDoc doc="privacy" />)} />
            <Route path="/terms" element={page(<LegalDoc doc="terms" />)} />
            {/* Permanent replacements for obsolete or mislabelled URLs. */}
            <Route path="/reports" element={<Navigate to="/impact/orphans-in-football" replace />} />
            <Route path="/safeguarding" element={<Navigate to="/refugees-in-football" replace />} />
            <Route path="/discover" element={page(<RouteLanding />)} />
            <Route path="/your-county" element={page(<RouteLanding />)} />
            <Route path="/national-plan" element={page(<RouteLanding />)} />
            <Route path="/partner" element={<Navigate to="/apply" replace />} />
            <Route path="/ecosystem" element={page(<EcosystemPage />)} />
            <Route path="/womens-game" element={page(<WomensGame />)} />
            <Route path="/womens-game/:slug" element={page(<WomanDetail />)} />
            <Route path="/training-and-jobs" element={page(<Opportunities />)} />
            <Route path="/projects" element={page(<Projects />)} />
            <Route path="/projects/:reference" element={page(<ProjectDetail />)} />
            <Route path="/specimen-heads-of-terms" element={page(<SpecimenTerms />)} />
            <Route path="/professionals" element={page(<Professionals />)} />
            <Route path="/what-if" element={page(<WhatIf />)} />
            <Route path="/how-we-work" element={page(<LegalDoc doc="how-we-work" />)} />
            <Route path="/valuations" element={page(<Valuations />)} />
            <Route path="/observations" element={page(<Observations />)} />
            <Route path="/legal" element={<Navigate to="/terms" replace />} />
            <Route path="/proposals" element={<Navigate to="/apply" replace />} />
            <Route path="/apply/track" element={<Navigate to="/apply" replace />} />
            <Route path="/governance" element={<Navigate to="/about" replace />} />
            <Route path="/sports-law" element={page(<SportsLaw />)} />
            <Route path="/corrections" element={<Navigate to="/sources" replace />} />
            <Route path="/sources" element={page(<Sources />)} />
            <Route path="/licences" element={page(<Licences />)} />
            <Route path="/court-rollout" element={page(<CourtRollout />)} />
            <Route path="/governors-league" element={page(<GovernorsLeague />)} />
            <Route path="/professionals/join" element={page(<ProfessionalsJoin />)} />
            <Route path="/fifa-ranking" element={page(<FifaRanking />)} />
            <Route path="/fkf-feed" element={page(<FkfFeed />)} />
            <Route path="/ranking-ambition" element={page(<RankingAmbition />)} />
            <Route path="/ranking-alerts/stop/:token" element={page(<RankingAlertStop />)} />
            <Route path="/harambee-fixtures" element={page(<HarambeeFixtures />)} />
            <Route path="/football-economy" element={page(<FootballEconomy />)} />
            <Route path="/contact" element={page(<Contact />)} />
            <Route path="/about" element={page(<About />)} />
            <Route path="/contribute" element={page(<Contribute />)} />
            <Route path="/modular-stadiums" element={page(<ModularStadiums />)} />
            <Route path="/apply" element={page(<Apply />)} />
            <Route path="/pitch-sizing" element={page(<PitchSizing />)} />
            <Route path="/lease-comparison" element={page(<LeaseComparison />)} />
            <Route path="/site-appraisal" element={page(<SiteAppraisal />)} />
            <Route path="/land-value/:county" element={page(<LandValue />)} />
            <Route path="/land-comparables" element={page(<LandComparables />)} />
            <Route path="/idle-acre" element={page(<IdleAcre />)} />
            <Route path="/compare-facilities" element={page(<CompareFacilities />)} />
            <Route path="/land-alerts" element={<Navigate to="/lease-comparison#land-alerts" replace />} />
            <Route path="/land-alerts/stop/:token" element={page(<LandAlertStop />)} />
            <Route path="/facility-planner" element={page(<FacilityPlanner />)} />
            <Route path="/land-audit" element={page(<LandAudit />)} />
            {STUBS.map(([slug, title, kicker, body]) => (
              <Route key={slug} path={`/${slug}`} element={page(<StubPage title={title} kicker={kicker} body={body} />)} />
            ))}

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="submissions" element={<Submissions />} />
              <Route path="intake" element={<Intake />} />
              <Route path="applications" element={<Applications />} />
              <Route path="discoveries" element={<Discoveries />} />
              <Route path="bio-review" element={<BioReview />} />
              <Route path="kenya-entity" element={<KenyaEntity />} />
              <Route path="licences" element={<AdminLicences />} />
              <Route path="projects" element={<AdminProjects />} />
              <Route path="consent-log" element={<ConsentLog />} />
              <Route path="data" element={<DataHealth />} />
              <Route path="search" element={<SearchConsole />} />
              <Route path="settings" element={<SiteSettings />} />
              <Route path=":resource" element={<ResourceAdmin />} />
            </Route>

            <Route
              path="*"
              element={page(
                <StubPage title="Page not found" kicker="404" body="That page has moved or never existed. Try one of these instead." />
              )}
            />
          </Routes>
        </AuthProvider>
        <Toaster theme="dark" position="bottom-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
